import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import { COUNTRIES, US_STATES } from '../constants/locations';
import axios from '../config/api';
import { API_URL } from '../config/api';

function CompleteProfilePage() {
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const [formData, setFormData] = useState({ country: 'United States', state: '', zipCode: '', city: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lookingUpCity, setLookingUpCity] = useState(false);
  const [cityDetected, setCityDetected] = useState(false);
  const [cityError, setCityError] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
    if (!loading && user && !user.needsProfileSetup) {
      navigate('/home', { replace: true });
    }
  }, [user, loading, navigate]);

  const isUSSelected = formData.country === 'United States';

  // Lookup city when user stops typing (debounced)
  useEffect(() => {
    if (!formData.city || formData.city.length < 2 || !isUSSelected) {
      setCityDetected(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLookingUpCity(true);
      setCityError('');
      try {
        const response = await axios.post(`${API_URL}/api/location/lookup-city`, { city: formData.city });
        setFormData(prev => ({
          ...prev,
          state: response.data.state,
          zipCode: response.data.zipCode,
        }));
        setCityDetected(true);
        setCityError('');
      } catch (err) {
        setCityDetected(false);
        if (err.response?.status === 404) {
          setCityError(err.response?.data?.detail || 'City not recognized');
        }
      } finally {
        setLookingUpCity(false);
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.city, isUSSelected]);

  const handleCityChange = (e) => {
    setFormData({ ...formData, city: e.target.value });
    setCityDetected(false);
    setCityError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.country) {
      setError('Please select your country');
      return;
    }
    if (isUSSelected && (!formData.city || formData.city.trim().length < 2)) {
      setError('Please enter your city');
      return;
    }
    if (isUSSelected && !formData.state) {
      setError('Please select your state or enter a valid city to auto-detect it');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/complete-profile`, {
        country: formData.country,
        state: isUSSelected ? formData.state : 'N/A',
        zipCode: isUSSelected ? formData.zipCode : 'N/A',
        city: isUSSelected ? formData.city : null,
      });
      await checkAuth();
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-civote flex items-center justify-center p-4" data-testid="complete-profile-page">
      <div className="bg-white rounded-card shadow-card w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-6">
          <Logo size="xlarge" />
          <h2 className="text-xl font-bold text-gray-800 font-rubik mt-4">Complete Your Profile</h2>
          <p className="text-gray-500 text-sm font-rubik mt-1">
            Welcome{user?.username ? `, ${user.username}` : ''}! Tell us where you're from to see relevant polls.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="complete-profile-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Country</label>
            <select
              value={formData.country}
              onChange={(e) => {
                const c = e.target.value;
                setFormData({ country: c, state: c === 'United States' ? '' : 'N/A', zipCode: c === 'United States' ? '' : 'N/A', city: '' });
                setCityError('');
                setCityDetected(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary bg-white font-rubik"
              required
              data-testid="profile-country-input"
            >
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {isUSSelected && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">City</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.city}
                    onChange={handleCityChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-rubik ${
                      cityError ? 'border-red-500' : cityDetected ? 'border-green-500' : 'border-gray-300'
                    }`}
                    required
                    placeholder="e.g. Bothell, Savoy, Springfield..."
                    data-testid="profile-city-input"
                  />
                  {lookingUpCity && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}
                </div>
                {cityError && <p className="text-red-600 text-xs mt-1">{cityError}</p>}
                {cityDetected && !cityError && formData.state && formData.zipCode && (
                  <p className="text-green-600 text-xs mt-1" data-testid="profile-city-detected">Detected: <strong>{formData.city}, {formData.state} {formData.zipCode}</strong></p>
                )}
                {!cityDetected && !cityError && formData.city.length < 2 && (
                  <p className="text-gray-500 text-xs mt-1">Type your city name to auto-detect state and zip code</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">State</label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary bg-white font-rubik"
                  required
                  data-testid="profile-state-input"
                >
                  <option value="">Select a state...</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {cityDetected && formData.state && (
                  <p className="text-gray-500 text-xs mt-1">Auto-filled from city. Change if incorrect.</p>
                )}
              </div>
            </>
          )}

          {error && (
            <div className="bg-red-50 text-danger p-3 rounded-card text-sm font-rubik" data-testid="profile-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-primary disabled:opacity-50"
            data-testid="complete-profile-submit"
          >
            {submitting ? 'Saving...' : 'Complete Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CompleteProfilePage;
